/**
 * TB Mukt Bharat Admin Dashboard
 * Government Portal - Fast & Lightweight
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Users, FileText, Video, UsersRound, BookOpen, Shield, TrendingUp, Activity, UserCheck, UserX, Ban, Clock, Newspaper, Trophy, Award, ScrollText } from 'lucide-react'

// ================================================================================
// TYPES
// ================================================================================
interface UserStats {
  totalUsers: number
  activeToday: number
  newUsers7Days: number
}

interface ContentStats {
  totalPosts: number
  totalReels: number
  totalGroups: number
  totalPages: number
}

// All-functionalities counts (from /admin/stats) — the "admin can see
// everything" overview. Active counts here are REAL last-activity based.
interface FunctionalityStats {
  totalNews: number
  totalCompetitions: number
  totalBadges: number
  totalCertificates: number
  activeNow: number
  activeThisWeek: number
  neverActive: number
}

interface UserBreakdown {
  byRole: Record<string, number>
  byType: Record<string, number>
  byStatus: { ACTIVE: number; INACTIVE: number; SUSPENDED: number; BANNED: number }
  newUsersToday: number
}

interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  username: string
  role: string
  status: string
  createdAt: string
}

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
const StatCard = React.memo(({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
  color: 'blue' | 'green' | 'purple' | 'orange' | 'cyan'
}) => {
  const colorStyles = useMemo(() => ({
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    cyan: 'bg-cyan-50 text-cyan-600',
  }), [])

  return (
    <GovCard className="flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${colorStyles[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value.toLocaleString()}</p>
      </div>
    </GovCard>
  )
})

// ================================================================================
// QUICK ACTION CARD
// ================================================================================
const QuickActionCard = React.memo(({
  icon: Icon,
  title,
  description,
  onClick
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  onClick: () => void
}) => (
  <GovCard className="cursor-pointer hover:border-blue-200 hover:shadow-md transition-all" onClick={onClick}>
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </GovCard>
))

// ================================================================================
// USER ROW COMPONENT
// ================================================================================
const UserRow = React.memo(({ user }: { user: User }) => {
  const roleStyles = useMemo(() => ({
    SUPER_ADMIN: 'bg-red-100 text-red-700',
    ADMIN: 'bg-blue-100 text-blue-700',
    USER: 'bg-gray-100 text-gray-700',
  }), [])

  const initials = useMemo(() =>
    `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`,
    [user.firstName, user.lastName]
  )

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-sm font-medium">
        {initials || 'U'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{user.firstName} {user.lastName}</p>
        <p className="text-xs text-gray-500 truncate">{user.email}</p>
      </div>
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleStyles[user.role as keyof typeof roleStyles] || roleStyles.USER}`}>
        {user.role}
      </span>
    </div>
  )
})

// ================================================================================
// ADMIN DASHBOARD
// ================================================================================
const AdminDashboard = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [userStats, setUserStats] = useState<UserStats>({ totalUsers: 0, activeToday: 0, newUsers7Days: 0 })
  const [contentStats, setContentStats] = useState<ContentStats>({ totalPosts: 0, totalReels: 0, totalGroups: 0, totalPages: 0 })
  const [funcStats, setFuncStats] = useState<FunctionalityStats>({
    totalNews: 0, totalCompetitions: 0, totalBadges: 0, totalCertificates: 0,
    activeNow: 0, activeThisWeek: 0, neverActive: 0,
  })
  const [recentUsers, setRecentUsers] = useState<User[]>([])
  const [breakdown, setBreakdown] = useState<UserBreakdown>({
    byRole: {},
    byType: {},
    byStatus: { ACTIVE: 0, INACTIVE: 0, SUSPENDED: 0, BANNED: 0 },
    newUsersToday: 0,
  })

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)

      // /admin/stats is the authoritative source for every platform count
      // (users, posts, reels, groups, news, competitions, badges, certificates,
      // activity). No hardcoded values; no community-scoped public-feed counts.
      const [usersRes, pagesRes, statsRes] = await Promise.all([
        api.get('/users?page=0&size=5').catch(() => ({ data: { success: false } })),
        api.get('/pages/popular?page=0&size=1').catch(() => ({ data: { success: false } })),
        api.get('/admin/stats').catch(() => ({ data: { success: false } })),
      ])

      // Recent users (for the sidebar list) — real recent registrations.
      if (usersRes.data.success) {
        setRecentUsers(usersRes.data.data.content || [])
      }

      // Authoritative platform counts + distributions from /admin/stats.
      if (statsRes.data.success && statsRes.data.data) {
        const s = statsRes.data.data
        setUserStats(prev => ({
          ...prev,
          totalUsers: typeof s.totalUsers === 'number' ? s.totalUsers : prev.totalUsers,
          activeToday: typeof s.activeUsers === 'number' ? s.activeUsers : prev.activeToday,
        }))
        setBreakdown({
          byRole: s.roleDistribution || {},
          byType: s.userTypeDistribution || {},
          byStatus: {
            ACTIVE: s.activeUsers || 0,
            INACTIVE: s.inactiveUsers || 0,
            SUSPENDED: s.suspendedUsers || 0,
            BANNED: s.bannedUsers || 0,
          },
          newUsersToday: s.newUsersToday || 0,
        })
        // All-functionalities counts (real activity-based where applicable)
        setFuncStats({
          totalNews: typeof s.totalNews === 'number' ? s.totalNews : 0,
          totalCompetitions: typeof s.totalCompetitions === 'number' ? s.totalCompetitions : 0,
          totalBadges: typeof s.totalBadges === 'number' ? s.totalBadges : 0,
          totalCertificates: typeof s.totalCertificates === 'number' ? s.totalCertificates : 0,
          activeNow: typeof s.activeNow === 'number' ? s.activeNow : 0,
          activeThisWeek: typeof s.activeThisWeek === 'number' ? s.activeThisWeek : 0,
          neverActive: typeof s.neverActive === 'number' ? s.neverActive : 0,
        })
        // Authoritative content totals from /admin/stats (NOT the community
        // public feed, which is scoped/filtered and undercounts).
        setContentStats(prev => ({
          ...prev,
          totalPosts: typeof s.totalPosts === 'number' ? s.totalPosts : prev.totalPosts,
          totalReels: typeof s.totalReels === 'number' ? s.totalReels : prev.totalReels,
          totalGroups: typeof s.totalGroups === 'number' ? s.totalGroups : prev.totalGroups,
        }))
      }

      // Pages count (real endpoint; not covered by /admin/stats).
      if (pagesRes.data.success) {
        setContentStats(prev => ({ ...prev, totalPages: pagesRes.data.data.totalElements || 0 }))
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Check auth on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
      navigate('/feed')
      return
    }

    fetchDashboardData()
  }, [user, isAuthenticated, navigate, fetchDashboardData])

  // Quick actions data
  const quickActions = useMemo(() => [
    { icon: Activity, title: 'Active Users', description: 'See who is online now', path: '/admin/active-users' },
    { icon: Users, title: 'User Management', description: 'Manage users & roles', path: '/admin/users' },
    { icon: UsersRound, title: 'Group Management', description: 'Oversee all community groups', path: '/admin/groups-admin' },
    { icon: FileText, title: 'Post Manager', description: 'Moderate all community posts', path: '/admin/posts' },
    { icon: Video, title: 'Reel Management', description: 'Moderate all reels', path: '/admin/reels-admin' },
    { icon: Shield, title: 'Reports', description: 'View reported content', path: '/admin/reports' },
    { icon: ScrollText, title: 'Oath Analytics', description: 'Who took the TB elimination pledge', path: '/admin/oaths' },
  ], [])

  // Loading state
  if (loading) {
    return (
      <GovLayout showSidebar={false}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        {/* Page Header */}
        <GovPageTitle
          title="Admin Dashboard"
          subtitle="Platform management & statistics"
          icon={Shield}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={Users} label="Total Users" value={userStats.totalUsers} color="blue" />
          <StatCard icon={FileText} label="Total Posts" value={contentStats.totalPosts} color="green" />
          <StatCard icon={Video} label="Total Reels" value={contentStats.totalReels} color="purple" />
          <StatCard icon={UsersRound} label="Groups" value={contentStats.totalGroups} color="orange" />
          <StatCard icon={BookOpen} label="Pages" value={contentStats.totalPages} color="cyan" />
        </div>

        {/* Functionalities Overview — all platform functions with counts */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Platform Functionalities</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={Activity} label="Active Now" value={funcStats.activeNow} color="green" />
            <StatCard icon={Newspaper} label="News" value={funcStats.totalNews} color="blue" />
            <StatCard icon={Trophy} label="Competitions" value={funcStats.totalCompetitions} color="orange" />
            <StatCard icon={Award} label="Badges" value={funcStats.totalBadges} color="purple" />
            <StatCard icon={ScrollText} label="Certificates" value={funcStats.totalCertificates} color="cyan" />
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Active this week: <strong className="text-gray-700">{funcStats.activeThisWeek.toLocaleString()}</strong></span>
            <span className="inline-flex items-center gap-1"><UserX className="w-3.5 h-3.5" /> Never active: <strong className="text-gray-700">{funcStats.neverActive.toLocaleString()}</strong></span>
          </div>
        </div>

        {/* User Breakdown - by role, type, status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* By Role */}
          <GovCard>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-900">Users by Role</h3>
            </div>
            <div className="space-y-2">
              {[
                { key: 'USER', label: 'User', color: 'bg-gray-100 text-gray-700' },
                { key: 'ADMIN', label: 'Admin', color: 'bg-blue-100 text-blue-700' },
                { key: 'SUPER_ADMIN', label: 'Super Admin', color: 'bg-red-100 text-red-700' },
              ].map(r => (
                <div key={r.key} className="flex items-center justify-between py-1.5">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.color}`}>{r.label}</span>
                  <span className="font-bold text-gray-900">{(breakdown.byRole[r.key] || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </GovCard>

          {/* By User Type */}
          <GovCard>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-emerald-600" />
              <h3 className="font-bold text-gray-900">Users by Type</h3>
            </div>
            <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
              {/* Render every user type the backend reports (USER + all
                  supporter/specialist types incl. NGO, Ni-kshay Mitra, the
                  three Supporter sub-types, Nutrition Sponsor, Volunteer,
                  Doctor, Health Worker, CSR Partner). Counts come straight
                  from /admin/stats userTypeDistribution — no hardcoding.
                  Capped height + scroll so this card matches its siblings
                  (By Role / By Status) instead of stretching the grid row. */}
              {[
                { key: 'USER', label: 'General User', color: 'bg-gray-400' },
                { key: 'NGO', label: 'NGO', color: 'bg-blue-500' },
                { key: 'NIKSHAY_MITRA', label: 'Ni-kshay Mitra', color: 'bg-emerald-500' },
                { key: 'SUPPORTER_INDIVIDUAL', label: 'Supporter (Individual)', color: 'bg-purple-500' },
                { key: 'SUPPORTER_NGO', label: 'Supporter (NGO)', color: 'bg-indigo-500' },
                { key: 'SUPPORTER_CORPORATE', label: 'Supporter (Corporate)', color: 'bg-cyan-500' },
                { key: 'NUTRITION_SPONSOR', label: 'Nutrition Sponsor', color: 'bg-amber-500' },
                { key: 'VOLUNTEER_COMMUNITY', label: 'Volunteer (Community)', color: 'bg-pink-500' },
                { key: 'DOCTOR', label: 'Doctor', color: 'bg-teal-500' },
                { key: 'HEALTH_WORKER', label: 'Health Worker', color: 'bg-lime-500' },
                { key: 'CSR_PARTNER', label: 'CSR Partner', color: 'bg-orange-500' },
              ].map(t => (
                <div key={t.key} className="flex items-center justify-between py-1.5">
                  <span className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} />
                    {t.label}
                  </span>
                  <span className="font-bold text-gray-900">{(breakdown.byType?.[t.key] || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </GovCard>

          {/* By Status */}
          <GovCard>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-5 h-5 text-cyan-600" />
              <h3 className="font-bold text-gray-900">Users by Status</h3>
            </div>
            <div className="space-y-2">
              {[
                { key: 'ACTIVE', label: 'Active', color: 'text-green-600', icon: UserCheck },
                { key: 'INACTIVE', label: 'Inactive', color: 'text-yellow-600', icon: Clock },
                { key: 'SUSPENDED', label: 'Suspended', color: 'text-orange-600', icon: UserX },
                { key: 'BANNED', label: 'Banned', color: 'text-red-600', icon: Ban },
              ].map(s => (
                <div key={s.key} className="flex items-center justify-between py-1.5">
                  <span className={`inline-flex items-center gap-2 text-sm ${s.color}`}>
                    <s.icon className="w-4 h-4" />
                    {s.label}
                  </span>
                  <span className="font-bold text-gray-900">{(breakdown.byStatus[s.key as keyof typeof breakdown.byStatus] || 0).toLocaleString()}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-1.5 border-t border-gray-100 mt-1 pt-2">
                <span className="inline-flex items-center gap-2 text-sm text-blue-600">
                  <TrendingUp className="w-4 h-4" />
                  New Today
                </span>
                <span className="font-bold text-gray-900">{breakdown.newUsersToday.toLocaleString()}</span>
              </div>
            </div>
          </GovCard>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {quickActions.map((action, index) => (
              <QuickActionCard
                key={index}
                icon={action.icon}
                title={action.title}
                description={action.description}
                onClick={() => navigate(action.path)}
              />
            ))}
          </div>
        </div>

        {/* Recent Users */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Recent Users</h2>
            <GovButton variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
              View All
            </GovButton>
          </div>

          <GovCard>
            {recentUsers.length === 0 ? (
              <GovEmptyState
                icon="👥"
                title="No Users Found"
                description="No users have registered yet"
              />
            ) : (
              <div className="divide-y divide-gray-100">
                {recentUsers.map((u) => (
                  <UserRow key={u.id} user={u} />
                ))}
              </div>
            )}
          </GovCard>
        </div>

        {/* Admin Quick Links */}
        <GovCard>
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-gray-900">Admin Quick Links</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              onClick={() => navigate('/admin/active-users')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Active Users
            </button>
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              User Management
            </button>
            <button
              onClick={() => navigate('/admin/groups-admin')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Group Management
            </button>
            <button
              onClick={() => navigate('/admin/posts')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Post Manager
            </button>
            <button
              onClick={() => navigate('/admin/reels-admin')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Reel Management
            </button>
            <button
              onClick={() => navigate('/admin/reports')}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Reports
            </button>
          </div>
        </GovCard>
      </div>
    </GovLayout>
  )
}

export default React.memo(AdminDashboard)